import { Tooltip } from "./Tooltip";
import { Link } from "react-router";
import { Sparkline } from "./Sparkline";
import { StatusIndicator, type StatusTone } from "./StatusIndicator";

export interface ServiceStatusWidgetProps {
  variant: "resource" | "server";
  name: string;
  identifier: string;
  status: { label: string; tone: StatusTone };
  metrics: readonly { label: string; value: string; tooltip?: string }[];
  samples?: readonly number[];
  chartLabel?: string;
  emptyChartLabel?: string;
  connection?: { label: string; value: string };
  action?: { label: string; to: string };
  actionPlacement?: "header" | "footer";
}

export function ServiceStatusWidget({
  variant,
  name,
  identifier,
  status,
  metrics,
  samples,
  chartLabel,
  emptyChartLabel = "Collecting live samples",
  connection,
  action,
  actionPlacement = "footer",
}: ServiceStatusWidgetProps) {
  return (
    <article
      className={`service-status-widget service-status-widget--${variant}`}
      aria-label={name}
    >
      <header className="service-status-widget-head">
        <h3>{name}</h3>
        <div className="service-status-widget-head-actions">
          {action && actionPlacement === "header" && (
            <Link className="btn btn--ghost btn--sm service-status-widget-action" to={action.to}>
              {action.label}
            </Link>
          )}
          <StatusIndicator label={status.label} tone={status.tone} />
        </div>
      </header>
      <p className="service-status-widget-id cell-mono">{identifier}</p>
      <div className="service-status-widget-chart">
        {samples && samples.length >= 2 ? (
          <Sparkline
            values={samples}
            width={260}
            height={48}
            stroke={variant === "server" ? "var(--accent-violet)" : "var(--accent-cyan)"}
            className="service-status-widget-spark"
            title={chartLabel}
          />
        ) : (
          <p>{emptyChartLabel}</p>
        )}
      </div>
      <dl className="service-status-widget-stats">
        {metrics.map(({ label, value, tooltip }) => (
          <div key={label}>
            <dt>
              {label}
              {tooltip && (
                <>
                  {" "}
                  <Tooltip label={`About ${label}`}>{tooltip}</Tooltip>
                </>
              )}
            </dt>
            <dd className="metric-value">{value}</dd>
          </div>
        ))}
      </dl>
      {(connection || (action && actionPlacement === "footer")) && (
        <footer className="service-status-widget-footer">
          {action && actionPlacement === "footer" && (
            <Link className="btn btn--ghost btn--sm service-status-widget-action" to={action.to}>
              {action.label}
            </Link>
          )}
          {connection && (
            <div className="service-status-widget-connection">
              <span>{connection.label}</span>
              <code>{connection.value}</code>
            </div>
          )}
        </footer>
      )}
    </article>
  );
}
